import { times } from "lodash";
import React, { CSSProperties } from "react";
import { StyleSheet, TouchableOpacity, View, ViewProps } from "react-native";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { SLImage } from "..";
import { PdArrowLeft, PdArrowRight } from "../Icons";
import { tReactiveImageProps } from "../ReactiveImage";

const Carousel: React.FC<
  ViewProps & Pick<tReactiveImageProps, "mobileStyle" | "webStyle"> & { imageUrls: string[]; defaultIndex?: number }
> = ({ children, style, mobileStyle, webStyle, imageUrls, defaultIndex = 0, ...rest }) => {
  const [currentIndex, setCurrentIndex] = React.useState(defaultIndex > imageUrls?.length - 1 ? 0 : defaultIndex);

  const imageUrl = imageUrls?.length > 0 ? imageUrls[currentIndex] : "";

  const handleOnPressPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleOnPressNext = () => {
    setCurrentIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : prev));
  };

  return (
    <View style={style} {...rest}>
      <SLImage
        source={{ uri: imageUrl }}
        mobileStyle={[styles.carousel, mobileStyle]}
        webStyle={webStyle ? { ...webStyles.carousel, ...webStyle } : webStyles.carousel}
      />
      {imageUrls.length > 1 && (
        <View style={styles.carouselDots}>
          {times(imageUrls.length).map((index) => (
            <View style={index === currentIndex ? styles.carouselDotSelected : styles.carouselDot} key={`${index}`} />
          ))}
        </View>
      )}
      {imageUrls.length > 1 && (
        <TouchableOpacity onPress={handleOnPressPrevious} style={styles.carouselPreviousButton}>
          <View style={styles.carouselButton}>
            <PdArrowLeft color={kColors.brandDark} style={styles.carouselIcon} />
          </View>
        </TouchableOpacity>
      )}
      {imageUrls.length > 1 && (
        <TouchableOpacity onPress={handleOnPressNext} style={styles.carouselNextButton}>
          <View style={styles.carouselButton}>
            <PdArrowRight color={kColors.brandDark} style={styles.carouselIcon} />
          </View>
        </TouchableOpacity>
      )}
      {children}
    </View>
  );
};

export default Carousel;

const styles = StyleSheet.create({
  carousel: {
    alignSelf: "stretch",
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 380,
  },
  carouselButton: {
    alignItems: "center",
    height: 30,
    justifyContent: "center",
    padding: 5,
    width: 30,
  },
  carouselDot: {
    ...kStyleConst.kDotDimensions,
    backgroundColor: kColors.brandDarkTransparent,
    borderRadius: 999,
    marginHorizontal: kStyleConst.kSpacing,
  },
  carouselDotSelected: {
    ...kStyleConst.kDotDimensions,
    backgroundColor: kColors.brandDark,
    borderRadius: 999,
    marginHorizontal: kStyleConst.kSpacing,
  },
  carouselDots: {
    bottom: 4 * kStyleConst.kSpacing,
    flexDirection: "row",
    position: "absolute",
    right: 3 * kStyleConst.kSpacing,
  },
  carouselIcon: {
    width: 22,
  },
  carouselNextButton: {
    alignItems: "center",
    bottom: 0,
    justifyContent: "center",
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
  },
  carouselPreviousButton: {
    alignItems: "center",
    bottom: 0,
    justifyContent: "center",
    left: 0,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    position: "absolute",
    top: 0,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  carousel: {
    alignSelf: "stretch",
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 380,
  },
} as const;
